import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  getRandomMeanings,
  getReviewSummary,
  getReviewWords,
  type ReviewQuality,
  type ReviewSchedule,
  type ReviewSummary,
  type ReviewWord,
  submitReviewResult,
} from "@/api/smartReview";
import { useAuthStore } from "@/store/authStore";

/** 복습 요약 (대시보드 카드 배지용) */
export function useReviewSummaryQuery() {
  const userId = useAuthStore(state => state.user?.id);

  return useQuery<ReviewSummary>({
    queryKey: ["reviewSummary", userId],
    queryFn: getReviewSummary,
    enabled: Boolean(userId),
    staleTime: 1000 * 60 * 5,
  });
}

/** 복습 단어 목록 (미니 퀴즈 진입 시) */
export function useReviewWordsQuery(enabled: boolean) {
  const userId = useAuthStore(state => state.user?.id);

  return useQuery<ReviewWord[]>({
    queryKey: ["reviewWords", userId],
    queryFn: () => getReviewWords(10),
    enabled: enabled && Boolean(userId),
    staleTime: 0,
  });
}

/** 보기 생성용 랜덤 meaning 조회 */
export function useRandomMeaningsQuery(setId: string | undefined, excludeMeaning: string | undefined) {
  return useQuery<string[]>({
    queryKey: ["randomMeanings", setId, excludeMeaning],
    queryFn: () => getRandomMeanings(setId!, excludeMeaning!),
    enabled: Boolean(setId) && Boolean(excludeMeaning),
    staleTime: 0,
  });
}

/** 복습 결과 제출 뮤테이션 */
export function useSubmitReviewMutation() {
  const queryClient = useQueryClient();
  const userId = useAuthStore(state => state.user?.id);

  return useMutation<ReviewSchedule, Error, { wordId: string; quality: ReviewQuality }>({
    mutationFn: ({ wordId, quality }) => submitReviewResult(wordId, quality),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["reviewSummary", userId] });
    },
  });
}
